/**
 * Persistent agent activity marker.
 *
 * This is deliberately separate from transcript events. Tool rows,
 * text blocks, and thinking blocks can replace/append normally while
 * this footer-level marker simply answers "is the agent still doing
 * work?" without pretending to be an incoming chat message.
 *
 * The ``'compact'`` variant repurposes the chrome for the ``/compact``
 * synchronous handshake: the CLI runs locally for a few seconds and
 * emits no per-iteration ``stream_event`` traffic, so we need a
 * dedicated affordance instead of "Working..." (which the user sees
 * as ambiguous when the agent isn't actually generating text).
 */

import { Scissors } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { AntiochAgentIcon } from './AntiochAgentIcon';
import { formatElapsed } from './blocks/elapsed';

export type WorkingIndicatorVariant = 'work' | 'compact';

export interface WorkingIndicatorProps {
  active: boolean;
  durationMs?: number | null;
  className?: string;
  variant?: WorkingIndicatorVariant;
}

export function WorkingIndicator({ active, durationMs, className, variant = 'work' }: WorkingIndicatorProps) {
  const activeLabel = variant === 'compact' ? 'Compacting' : 'Working';
  const label = active ? activeLabel : `Worked for ${formatElapsed(durationMs ?? 0)}`;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={active ? `${activeLabel}…` : label}
      className={cn(
        'flex min-h-[1lh] w-fit select-none items-center gap-1 rounded-md py-0.5',
        'font-normal text-[13px] text-gray-400 leading-relaxed dark:text-gray-500',
        className
      )}
    >
      {variant === 'compact' ? (
        <Scissors className="size-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
      ) : (
        <AntiochAgentIcon className="size-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
      )}
      <span
        className={cn(
          active &&
            'animate-working-shimmer bg-[length:220%_100%] bg-[linear-gradient(110deg,var(--color-gray-400)_0%,var(--color-gray-400)_35%,var(--color-gray-200)_50%,var(--color-gray-400)_65%,var(--color-gray-400)_100%)] bg-clip-text text-transparent dark:bg-[linear-gradient(110deg,var(--color-gray-500)_0%,var(--color-gray-500)_35%,var(--color-gray-300)_50%,var(--color-gray-500)_65%,var(--color-gray-500)_100%)]'
        )}
      >
        {label}
      </span>
    </div>
  );
}
